import NepaliDate from 'nepali-date-converter'

/** Locale/zone context set from the signed-in hospital's settings. */
export const locale = {
  timeZone: 'Asia/Kathmandu',
  currency: 'NPR',
  showBs: true,
}

export function configureLocale(opts: Partial<typeof locale>) {
  Object.assign(locale, opts)
}

const moneyFmt = new Map<string, Intl.NumberFormat>()

/** Money is exchanged with the API as strings; never use floats for arithmetic on the client. */
export function money(value: string | number | null | undefined, currency = locale.currency, opts?: { compact?: boolean }) {
  if (value === null || value === undefined || value === '') return '—'
  const n = typeof value === 'string' ? Number(value) : value
  if (Number.isNaN(n)) return '—'
  const key = `${currency}:${opts?.compact ? 'c' : 'f'}`
  let f = moneyFmt.get(key)
  if (!f) {
    f = new Intl.NumberFormat(currency === 'NPR' ? 'en-IN' : 'en-US', {
      style: 'currency',
      currency,
      currencyDisplay: currency === 'NPR' ? 'code' : 'symbol',
      maximumFractionDigits: opts?.compact ? 1 : 2,
      minimumFractionDigits: opts?.compact ? 0 : 2,
      notation: opts?.compact ? 'compact' : 'standard',
    })
    moneyFmt.set(key, f)
  }
  return f.format(n).replace('NPR', 'Rs.').replace(/\u00a0/g, ' ')
}

export function num(value: number | null | undefined, digits = 0) {
  if (value === null || value === undefined) return '—'
  return new Intl.NumberFormat('en-IN', { maximumFractionDigits: digits }).format(value)
}

function toDate(v: string | Date) {
  return typeof v === 'string' ? new Date(v) : v
}

export function dateTime(v: string | Date | null | undefined) {
  if (!v) return '—'
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: locale.timeZone,
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(toDate(v))
}

export function time(v: string | Date | null | undefined) {
  if (!v) return '—'
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: locale.timeZone,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(toDate(v))
}

/** A date-only value (YYYY-MM-DD) or a timestamp rendered as a local calendar date. */
export function date(v: string | Date | null | undefined) {
  if (!v) return '—'
  if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v)) {
    const [y, m, d] = v.split('-').map(Number)
    return new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC' }).format(
      new Date(Date.UTC(y, m - 1, d)),
    )
  }
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: locale.timeZone,
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(toDate(v))
}

/** Bikram Sambat (Nepali calendar) representation, e.g. "2083 Asar 32". */
export function bsDate(v: string | Date | null | undefined, devanagari = false): string | null {
  if (!v) return null
  try {
    let d: Date
    if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v)) {
      const [y, m, dd] = v.split('-').map(Number)
      d = new Date(y, m - 1, dd)
    } else {
      const parts = new Intl.DateTimeFormat('en-CA', { timeZone: locale.timeZone }).format(toDate(v)).split('-').map(Number)
      d = new Date(parts[0], parts[1] - 1, parts[2])
    }
    const nd = new NepaliDate(d)
    return nd.format(devanagari ? 'YYYY MMMM DD' : 'YYYY MMMM DD', devanagari ? 'np' : 'en')
  } catch {
    return null
  }
}

/** Today's local date in the hospital time zone, as YYYY-MM-DD. */
export function todayISO(offsetDays = 0) {
  const d = new Date(Date.now() + offsetDays * 86400000)
  return new Intl.DateTimeFormat('en-CA', { timeZone: locale.timeZone }).format(d)
}

export function relative(v: string | Date | null | undefined) {
  if (!v) return '—'
  const diff = (Date.now() - toDate(v).getTime()) / 1000
  const rtf = new Intl.RelativeTimeFormat('en', { numeric: 'auto' })
  const abs = Math.abs(diff)
  if (abs < 60) return rtf.format(-Math.round(diff), 'second')
  if (abs < 3600) return rtf.format(-Math.round(diff / 60), 'minute')
  if (abs < 86400) return rtf.format(-Math.round(diff / 3600), 'hour')
  return rtf.format(-Math.round(diff / 86400), 'day')
}

export function minutesSince(v: string | null | undefined) {
  if (!v) return null
  return Math.max(0, Math.round((Date.now() - new Date(v).getTime()) / 60000))
}

export function initials(name: string | null | undefined) {
  if (!name) return '?'
  const parts = name.replace(/^(Dr\.|Mr\.|Ms\.|Mrs\.)\s*/i, '').trim().split(/\s+/)
  return ((parts[0]?.[0] ?? '') + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase()
}

export function titleCase(s: string | null | undefined) {
  if (!s) return ''
  return s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}

export function ageSex(age: number | null | undefined, sex: string | null | undefined) {
  const a = age === null || age === undefined ? '' : `${age}y`
  const s = sex ? sex[0].toUpperCase() : ''
  return [a, s].filter(Boolean).join(' / ')
}

/** "Chrome on macOS" from a User-Agent string. Order matters: Edge and Opera also say "Chrome", and Chrome also says "Safari". */
export function deviceLabel(ua: string | null | undefined): string {
  if (!ua) return 'Unknown device'
  const browser =
    /Edg(e|A|iOS)?\//.test(ua) ? 'Edge'
    : /OPR\/|Opera/.test(ua) ? 'Opera'
    : /Firefox\/|FxiOS\//.test(ua) ? 'Firefox'
    : /SamsungBrowser\//.test(ua) ? 'Samsung Internet'
    : /Chrome\/|CriOS\//.test(ua) ? 'Chrome'
    : /Safari\//.test(ua) && /Version\//.test(ua) ? 'Safari'
    : null
  const os =
    /iPhone/.test(ua) ? 'iPhone'
    : /iPad/.test(ua) ? 'iPad'
    : /Android/.test(ua) ? 'Android'
    : /Windows NT/.test(ua) ? 'Windows'
    : /Mac OS X|Macintosh/.test(ua) ? 'macOS'
    : /CrOS/.test(ua) ? 'ChromeOS'
    : /Linux/.test(ua) ? 'Linux'
    : null
  if (browser && os) return `${browser} on ${os}`
  return browser ?? os ?? 'Unknown device'
}
